'use strict';

const { queryAll, queryFirst, addClass, removeClass, hasClass } = require('../domUtil');
const { breakpoints, HIDDEN_CLASS } = require('../constants');

const COMPONENT_SELECTOR = '.js-showmore';
const INITIALIZED_CLASS = 'm-initialized';
const ITEMS_SELECTOR = '.js-showmore-items';
const BUTTON_CLASS = 'js-showmore-button';
const END_MESSAGE_CLASS = 'showmore-end-message';
const DIVIDER_CLASS = 'm-divider';
const ACTION_CONTAINER_CLASS = 'js-showmore-action';
const ACTIONS_CLASS = 'showmore'; // for css styling
const NON_MODULE_SELECTOR = '.clp-items-footer, .search-footer, script, style, noscript';

let clickHandlerAttached = false;
let resizeTimer;

/**
 * @param {HTMLElement} container
 * @returns {number}
 */
function getChunkSize(container) {
    const { dataset } = container;
    const width = window.innerWidth;

    if (width >= breakpoints.lg) {
        return parseInt(dataset.showmoreDesktop, 10);
    }

    if (width >= breakpoints.md) {
        return parseInt(dataset.showmoreTablet, 10);
    }

    return parseInt(dataset.showmoreMobile, 10);
}

/**
 * @param {HTMLElement} container
 * @returns {boolean}
 */
function isConfigured(container) {
    const { dataset } = container;

    return ['showmoreMobile', 'showmoreTablet', 'showmoreDesktop'].some(
        key => parseInt(dataset[key], 10) > 0
    );
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getTiles(container) {
    const itemsContainer = queryFirst(ITEMS_SELECTOR, container);

    if (!itemsContainer) {
        return [];
    }

    return Array.from(itemsContainer.children);
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function hasVisibleContent(element) {
    if (!element || element.nodeType !== 1 || element.matches(NON_MODULE_SELECTOR)) {
        return false;
    }

    const style = window.getComputedStyle(element);

    if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
    }

    if (element.children.length > 0) {
        return Array.from(element.children).some(child => hasVisibleContent(child));
    }

    return element.textContent.trim().length > 0;
}

/**
 * @param {HTMLElement} container
 * @returns {boolean}
 */
function hasFollowingModule(container) {
    let sibling = container.nextElementSibling;

    while (sibling) {
        if (sibling.matches('.clp-items-footer, .search-footer') || sibling.tagName === 'FOOTER') {
            return false;
        }

        if (hasVisibleContent(sibling)) {
            return true;
        }

        sibling = sibling.nextElementSibling;
    }

    return false;
}

/**
 * The divider closes off the module once everything is expanded
 * @param {HTMLElement} container
 */
function updateDivider(container) {
    const actions = queryFirst(`.${ACTION_CONTAINER_CLASS}`, container);

    if (!actions) {
        return;
    }

    const endMessage = queryFirst(`.${END_MESSAGE_CLASS}`, actions);

    if (endMessage && hasFollowingModule(container)) {
        addClass(actions, DIVIDER_CLASS);
        return;
    }

    removeClass(actions, DIVIDER_CLASS);
}

function updateAllDividers() {
    queryAll(COMPONENT_SELECTOR).forEach(updateDivider);
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement}
 */
function ensureActionContainer(container) {
    let actions = queryFirst(`.${ACTION_CONTAINER_CLASS}`, container);

    if (!actions) {
        actions = document.createElement('div');
        addClass(actions, ACTION_CONTAINER_CLASS, ACTIONS_CLASS);

        const itemsContainer = queryFirst(ITEMS_SELECTOR, container);

        if (itemsContainer) {
            itemsContainer.after(actions);
        } else {
            container.appendChild(actions);
        }
    }

    removeClass(actions, HIDDEN_CLASS);
    return actions;
}

/**
 * @param {HTMLElement} container
 */
function showButton(container) {
    const actions = ensureActionContainer(container);
    const endMessage = queryFirst(`.${END_MESSAGE_CLASS}`, actions);

    if (endMessage) {
        endMessage.remove();
    }

    let button = queryFirst(`.${BUTTON_CLASS}`, actions);

    if (!button) {
        const label = (container.dataset.showmoreLabel || 'Show More').trim();

        button = document.createElement('button');
        button.type = 'button';
        button.className = `btn btn-small button-secondary ${BUTTON_CLASS}`;
        button.textContent = label;
        actions.appendChild(button);
    }
}

/**
 * @param {HTMLElement} container
 */
function showEndMessage(container) {
    const actions = ensureActionContainer(container);
    const button = queryFirst(`.${BUTTON_CLASS}`, actions);

    if (button) {
        button.remove();
    }

    let endMessage = queryFirst(`.${END_MESSAGE_CLASS}`, actions);

    if (!endMessage) {
        const label = (container.dataset.showmoreEndLabel || 'That\u2019s everything for now.').trim();

        endMessage = document.createElement('p');
        addClass(endMessage, END_MESSAGE_CLASS);
        endMessage.textContent = label;
        actions.appendChild(endMessage);
    }
}

/**
 * @param {HTMLElement} container
 */
function removeActions(container) {
    const actions = queryFirst(`.${ACTION_CONTAINER_CLASS}`, container);

    if (actions) {
        actions.remove();
    }
}

/**
 * @param {HTMLElement} tile
 * @param {boolean} isVisible
 */
function setTileVisibility(tile, isVisible) {
    if (isVisible) {
        removeClass(tile, HIDDEN_CLASS);
        tile.removeAttribute('aria-hidden');
        return;
    }

    addClass(tile, HIDDEN_CLASS);
    tile.setAttribute('aria-hidden', 'true');
}

/**
 * @param {HTMLElement} container
 */
function updateVisibility(container) {
    const tiles = getTiles(container);
    const chunkSize = getChunkSize(container);

    if (!isConfigured(container) || !chunkSize || tiles.length <= chunkSize) {
        tiles.forEach(tile => setTileVisibility(tile, true));
        removeActions(container);
        delete container.dataset.showmoreVisible;
        updateDivider(container);
        return;
    }

    const visibleCount = Math.min(
        parseInt(container.dataset.showmoreVisible, 10) || chunkSize,
        tiles.length
    );

    tiles.forEach((tile, index) => {
        setTileVisibility(tile, index < visibleCount);
    });

    if (visibleCount < tiles.length) {
        showButton(container);
    } else {
        showEndMessage(container);
    }

    container.dataset.showmoreVisible = String(visibleCount);
    updateDivider(container);
}

/**
 * @param {HTMLElement} container
 */
function resetContainer(container) {
    delete container.dataset.showmoreVisible;
    updateVisibility(container);
}

/**
 * @param {HTMLElement} container
 */
function onShowMoreClick(container) {
    const chunkSize = getChunkSize(container);
    const currentVisible = parseInt(container.dataset.showmoreVisible, 10) || chunkSize;

    container.dataset.showmoreVisible = String(currentVisible + chunkSize);
    updateVisibility(container);
}

function attachGlobalClickHandler() {
    if (clickHandlerAttached) {
        return;
    }

    clickHandlerAttached = true;

    document.addEventListener('click', e => {
        const button = e.target.closest(`.${BUTTON_CLASS}`);

        if (!button) {
            return;
        }

        const container = button.closest(COMPONENT_SELECTOR);

        if (!container) {
            return;
        }

        e.preventDefault();
        onShowMoreClick(container);
    });
}

/**
 * @param {ParentNode} [root]
 */
function initShowMore(root = document) {
    queryAll(COMPONENT_SELECTOR, root).forEach(container => {
        if (hasClass(container, INITIALIZED_CLASS) || !isConfigured(container)) {
            return;
        }

        addClass(container, INITIALIZED_CLASS);
        resetContainer(container);
    });
}

function reinitShowMore() {
    queryAll(COMPONENT_SELECTOR).forEach(container => {
        removeClass(container, INITIALIZED_CLASS);
        resetContainer(container);

        if (isConfigured(container)) {
            addClass(container, INITIALIZED_CLASS);
        }
    });
}

function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(reinitShowMore, 150);
}

function init() {
    attachGlobalClickHandler();
    initShowMore();

    document.addEventListener('slot-loaded', e => {
        initShowMore(e.target);
        updateAllDividers();
    });

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
}

module.exports = { init };
